import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchBackgroundSounds, type BackgroundSound } from "@/api/client";
import { DraggableExpressionChip, EXPRESSION_PRESETS } from "./ExpressionVocale";

export type ContentChunk =
  | { type: "text"; value: string }
  | { type: "tag"; value: string; variant: "respiration" | "effet-sonore" | "voix" };

export function parseTaggedText(text: string, hideEffetSonore = false): ContentChunk[] {
  const chunks: ContentChunk[] = [];
  const regex = /( \{[^}]+\}|\[[^\]]+\])/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      chunks.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    const tag = match[1];
    if (tag.startsWith("{") || tag.startsWith(" {")) {
      if (hideEffetSonore) {
        chunks.push({ type: "text", value: tag });
      } else {
        chunks.push({ type: "tag", value: tag.replace(/^ ?\{|\}$/g, ""), variant: "effet-sonore" });
      }
    } else {
      const inner = tag.slice(1, -1);
      if (/^(AMBIANCE|TRANSITION\s+SONORE)\s*:/i.test(inner)) {
        // production-only markers — hidden from user-facing view
      } else if (/^(pause|silence|respiration)/i.test(inner)) {
        chunks.push({ type: "tag", value: inner, variant: "respiration" });
      } else {
        chunks.push({ type: "tag", value: inner, variant: "voix" });
      }
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    chunks.push({ type: "text", value: text.slice(lastIndex) });
  }
  return chunks;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function valueToHTML(text: string, hideEffetSonore = false): string {
  if (!text) return "";
  const chunks = parseTaggedText(text, false);
  return chunks
    .map((chunk) => {
      if (chunk.type === "text") return escapeHtml(chunk.value);
      if (hideEffetSonore && chunk.variant === "effet-sonore")
        return escapeHtml(` {${chunk.value}}`);

      const isGenuineTag =
        chunk.variant === "effet-sonore" ||
        chunk.variant === "respiration" ||
        (chunk.variant === "voix" &&
          !chunk.value.includes(":") &&
          !chunk.value.includes("«") &&
          !chunk.value.includes("»") &&
          chunk.value.length < 60);

      if (!isGenuineTag) {
        return escapeHtml(`[${chunk.value}]`);
      }

      const chipClass =
        chunk.variant === "respiration"
          ? "inline-flex items-center rounded-[4px] border border-[#623DC7] bg-[#E1D6FF] px-2 py-1 text-[12px] font-medium text-[#2F1E64]"
          : chunk.variant === "effet-sonore"
          ? "inline-flex items-center rounded-[4px] border border-[#C8009C] bg-[#FDEBF9] px-2 py-1 text-[12px] font-medium text-[#7D005F]"
          : "inline-flex items-center rounded-[4px] border border-[#623DC7] bg-[#E1D6FF] px-2 py-1 text-[12px] font-medium text-[#2F1E64]";
      const rawTag = chunk.variant === "effet-sonore" ? ` {${chunk.value}}` : `[${chunk.value}]`;
      const displayLabel = chunk.variant === "effet-sonore"
        ? (chunk.value.includes("/") ? chunk.value.split("/")[0] : (chunk.value || "Effet Sonore"))
        : chunk.value;

      const isSound = chunk.variant === "effet-sonore";
      const iconMarkup = isSound
        ? ""
        : `<svg class="mr-1.5 h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.94584 5.45489C3.94584 5.10343 4.23074 4.81852 4.5822 4.81852C4.93365 4.81852 5.21856 5.10343 5.21856 5.45489V10.5458C5.21856 10.8972 4.93365 11.1821 4.5822 11.1821C4.23074 11.1821 3.94584 10.8972 3.94584 10.5458V5.45489ZM1.40039 7.36397C1.40039 7.01252 1.6853 6.72761 2.03675 6.72761C2.3882 6.72761 2.67311 7.01252 2.67311 7.36397V8.63669C2.67311 8.98814 2.3882 9.27305 2.03675 9.27305C1.6853 9.27305 1.40039 8.98814 1.40039 8.63669V7.36397ZM6.49128 2.27308C6.49128 1.92163 6.77619 1.63672 7.12764 1.63672C7.4791 1.63672 7.764 1.92163 7.764 2.27308V12.4549C7.764 12.8063 7.4791 13.0912 7.12764 13.0912C6.77619 13.0912 6.49128 12.8063 6.49128 12.4549V2.27308ZM9.03673 3.5458C9.03673 3.19435 9.32163 2.90944 9.67309 2.90944C10.0245 2.90944 10.3094 3.19435 10.3094 3.5458V13.7276C10.3094 14.079 10.0245 14.3639 9.67309 14.3639C9.32163 14.3639 9.03673 14.079 9.03673 13.7276V3.5458ZM11.5822 5.45489C11.5822 5.10343 11.8671 4.81852 12.2185 4.81852C12.57 4.81852 12.8549 5.10343 12.8549 5.45489V10.5458C12.8549 10.8972 12.57 11.1821 12.2185 11.1821C11.8671 11.1821 11.5822 10.8972 11.5822 10.5458V5.45489ZM14.1276 7.36397C14.1276 7.01252 14.4125 6.72761 14.764 6.72761C15.1154 6.72761 15.4003 7.01252 15.4003 7.36397V8.63669C15.4003 8.98814 15.1154 9.27305 14.764 9.27305C14.4125 9.27305 14.1276 8.98814 14.1276 8.63669V7.36397ZM14.1276 7.36397C14.1276 7.01252 14.4125 6.72761 14.764 6.72761C15.1154 6.72761 15.4003 7.01252 15.4003 7.36397V8.63669C15.4003 8.98814 15.1154 9.27305 14.764 9.27305C14.4125 9.27305 14.1276 8.98814 14.1276 8.63669V7.36397Z" fill="currentColor"/></svg>`;
      return `<span contenteditable="false" data-tag="${escapeHtml(rawTag)}" class="${chipClass}">${iconMarkup}${escapeHtml(displayLabel)}</span>`;
    })
    .join("");
}

export function divToText(div: HTMLElement): string {
  let result = "";
  function traverse(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent ?? "";
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset.tag !== undefined) {
        result += el.dataset.tag;
      } else if (el.tagName === "BR") {
        result += "\n";
      } else {
        const isBlock = el.tagName === "DIV" || el.tagName === "P";
        if (isBlock && result.length > 0 && !result.endsWith("\n")) result += "\n";
        el.childNodes.forEach(traverse);
      }
    }
  }
  div.childNodes.forEach(traverse);
  return result;
}

type TaggedTextareaProps = {
  value: string;
  onChange: (text: string) => void;
  hideEffetSonore: boolean;
  placeholder?: string;
  heldTag?: string | null;
  onTagPlaced?: () => void;
  onPickTag?: (rawTag: string) => void;
};

export function TaggedTextarea({
  value,
  onChange,
  hideEffetSonore,
  placeholder,
  heldTag,
  onTagPlaced,
  onPickTag,
}: TaggedTextareaProps) {
  const ref = useRef<HTMLDivElement>(null);
  const internalRef = useRef<string | null>(null);
  const [bracePopup, setBracePopup] = useState<{ x: number; y: number; savedRange: Range } | null>(null);
  const [braceCustom, setBraceCustom] = useState("");
  const braceInputRef = useRef<HTMLInputElement>(null);
  const bracePopupRef = useRef<HTMLDivElement>(null);

  const [soundPopup, setSoundPopup] = useState<{ x: number; y: number; savedRange: Range } | null>(null);
  const [soundSearch, setSoundSearch] = useState("");
  const [sounds, setSounds] = useState<BackgroundSound[]>([]);
  const [soundsLoading, setSoundsLoading] = useState(false);
  const soundSearchRef = useRef<HTMLInputElement>(null);
  const soundPopupRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    if (value !== internalRef.current) {
      ref.current.innerHTML = valueToHTML(value, hideEffetSonore);
      internalRef.current = value;
    }
  }, [value, hideEffetSonore]);

  useEffect(() => {
    if (!bracePopup) return;
    const handler = (e: MouseEvent) => {
      if (bracePopupRef.current && !bracePopupRef.current.contains(e.target as Node)) {
        setBracePopup(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [bracePopup]);

  useEffect(() => {
    if (!soundPopup) return;
    setSoundsLoading(true);
    fetchBackgroundSounds()
      .then((list) => {
        setSounds(list);
        setSoundsLoading(false);
      })
      .catch(() => setSoundsLoading(false));
    setTimeout(() => soundSearchRef.current?.focus(), 50);
  }, [soundPopup]);

  useEffect(() => {
    if (!soundPopup) return;
    const handler = (e: MouseEvent) => {
      if (soundPopupRef.current && !soundPopupRef.current.contains(e.target as Node)) {
        setSoundPopup(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [soundPopup]);

  const createChip = (dataTag: string, label: string, chipClass: string) => {
    const span = document.createElement("span");
    span.setAttribute("contenteditable", "false");
    span.dataset.tag = dataTag;
    span.className = chipClass;
    const isSound = dataTag.startsWith(" {");
    const iconMarkup = isSound
      ? ""
      : `<svg class="mr-1.5 h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.94584 5.45489C3.94584 5.10343 4.23074 4.81852 4.5822 4.81852C4.93365 4.81852 5.21856 5.10343 5.21856 5.45489V10.5458C5.21856 10.8972 4.93365 11.1821 4.5822 11.1821C4.23074 11.1821 3.94584 10.8972 3.94584 10.5458V5.45489ZM1.40039 7.36397C1.40039 7.01252 1.6853 6.72761 2.03675 6.72761C2.3882 6.72761 2.67311 7.01252 2.67311 7.36397V8.63669C2.67311 8.98814 2.3882 9.27305 2.03675 9.27305C1.6853 9.27305 1.40039 8.98814 1.40039 8.63669V7.36397ZM6.49128 2.27308C6.49128 1.92163 6.77619 1.63672 7.12764 1.63672C7.4791 1.63672 7.764 1.92163 7.764 2.27308V12.4549C7.764 12.8063 7.4791 13.0912 7.12764 13.0912C6.77619 13.0912 6.49128 12.8063 6.49128 12.4549V2.27308ZM9.03673 3.5458C9.03673 3.19435 9.32163 2.90944 9.67309 2.90944C10.0245 2.90944 10.3094 3.19435 10.3094 3.5458V13.7276C10.3094 14.079 10.0245 14.3639 9.67309 14.3639C9.32163 14.3639 9.03673 14.079 9.03673 13.7276V3.5458ZM11.5822 5.45489C11.5822 5.10343 11.8671 4.81852 12.2185 4.81852C12.57 4.81852 12.8549 5.10343 12.8549 5.45489V10.5458C12.8549 10.8972 12.57 11.1821 12.2185 11.1821C11.8671 11.1821 11.5822 10.8972 11.5822 10.5458V5.45489ZM14.1276 7.36397C14.1276 7.01252 14.4125 6.72761 14.764 6.72761C15.1154 6.72761 15.4003 7.01252 15.4003 7.36397V8.63669C15.4003 8.98814 15.1154 9.27305 14.764 9.27305C14.4125 9.27305 14.1276 8.98814 14.1276 8.63669V7.36397Z" fill="currentColor"/></svg>`;
    span.innerHTML = `${iconMarkup}${escapeHtml(label)}`;
    return span;
  };

  const insertTagAtRange = (range: Range, rawTag: string) => {
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);

    const trimmed = rawTag.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      const inner = trimmed.slice(1, -1);
      const chipClass = /^(pause|silence|respiration)/i.test(inner)
        ? "inline-flex items-center rounded-[4px] border border-[#623DC7] bg-[#E1D6FF] px-2 py-1 text-[12px] font-medium text-[#2F1E64]"
        : "inline-flex items-center rounded-[4px] border border-[#623DC7] bg-[#E1D6FF] px-2 py-1 text-[12px] font-medium text-[#2F1E64]";
      if (rawTag.startsWith(" ")) {
        const space = document.createTextNode(" ");
        range.insertNode(space);
        range.setStartAfter(space);
      }
      const chip = createChip(trimmed, inner, chipClass);
      range.insertNode(chip);
      range.setStartAfter(chip);
    } else if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      const inner = trimmed.slice(1, -1);
      const label = inner.includes("/") ? inner.split("/")[0] : (inner || "Effet Sonore");
      const chip = createChip(
        rawTag,
        label,
        "inline-flex items-center rounded-[4px] border border-[#C8009C] bg-[#FDEBF9] px-2 py-1 text-[12px] font-medium text-[#7D005F]"
      );
      range.insertNode(chip);
      range.setStartAfter(chip);
    } else {
      range.insertNode(document.createTextNode(rawTag));
    }

    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);

    const text = divToText(ref.current!);
    internalRef.current = text;
    onChange(text);
  };

  const insertTagAtPoint = (x: number, y: number, rawTag: string) => {
    const doc = document as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
      caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    };
    let range: Range | null = null;
    if (doc.caretRangeFromPoint) {
      range = doc.caretRangeFromPoint(x, y);
    } else if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(x, y);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }
    if (!range) return;
    insertTagAtRange(range, rawTag);
  };

  const handleBraceInsert = (rawTag: string) => {
    if (!bracePopup) return;
    insertTagAtRange(bracePopup.savedRange, rawTag);
    setBracePopup(null);
    setBraceCustom("");
    setTimeout(() => ref.current?.focus(), 0);
  };

  const SOUND_CHIP =
    "inline-flex items-center rounded-[4px] border border-[#C8009C] bg-[#FDEBF9] px-2 py-1 text-[12px] font-medium text-[#7D005F]";

  const handleSoundInsert = (sound: BackgroundSound) => {
    if (!soundPopup) return;
    const label = sound.category || sound.name;
    const rawTag = ` {${sound.path}}`;
    const range = soundPopup.savedRange;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    const chip = createChip(rawTag, label, SOUND_CHIP);
    range.insertNode(chip);
    range.setStartAfter(chip);
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
    const text = divToText(ref.current!);
    internalRef.current = text;
    onChange(text);
    setSoundPopup(null);
    setSoundSearch("");
    setTimeout(() => ref.current?.focus(), 0);
  };

  return (
    <>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={(e) => {
          const text = divToText(e.currentTarget as HTMLDivElement);
          internalRef.current = text;
          onChange(text);
        }}
        onKeyDown={(e) => {
          if (e.key === "{") {
            e.preventDefault();
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
              const r = sel.getRangeAt(0).cloneRange();
              const rect = r.getBoundingClientRect();
              setSoundPopup({ x: rect.left, y: rect.bottom, savedRange: r });
              setSoundSearch("");
            }
            return;
          }
          if (e.key === "[") {
            e.preventDefault();
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
              const r = sel.getRangeAt(0).cloneRange();
              const rect = r.getBoundingClientRect();
              setBracePopup({ x: rect.left, y: rect.bottom, savedRange: r });
              setBraceCustom("");
              setTimeout(() => braceInputRef.current?.focus(), 50);
            }
            return;
          }
          if (e.key === "Enter") {
            e.preventDefault();
            document.execCommand("insertText", false, "\n");
          }
        }}
        onPaste={(e) => {
          e.preventDefault();
          document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
        }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          const chipEl = target.closest("[data-tag]") as HTMLElement | null;
          if (chipEl && !heldTag) {
            // Pick up existing chip
            e.preventDefault();
            const rawTag = chipEl.dataset.tag!;

            // Clean up the preceding space if there is one!
            let prevNode = chipEl.previousSibling;
            if (prevNode && prevNode.nodeType === Node.TEXT_NODE) {
              const val = prevNode.textContent || "";
              if (val.endsWith(" ")) {
                prevNode.textContent = val.slice(0, -1);
              }
            }

            chipEl.remove();
            const text = divToText(ref.current!);
            internalRef.current = text;
            onChange(text);
            onPickTag?.(rawTag);
            return;
          }
          if (heldTag) {
            // Place held tag at click coordinates
            e.preventDefault();
            insertTagAtPoint(e.clientX, e.clientY, heldTag);
            onTagPlaced?.();
          }
        }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("text/plain")) e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const raw = e.dataTransfer.getData("text/plain");
          if (raw) insertTagAtPoint(e.clientX, e.clientY, raw);
        }}
        className={`min-h-[72px] w-full whitespace-pre-wrap break-words rounded-md border border-transparent bg-transparent px-1 text-[14px] font-normal leading-6 text-[#334155] outline-none transition-colors hover:border-[#E2E8F0] focus:border-[#007AFF] empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none ${
          heldTag ? "cursor-crosshair" : ""
        }`}
      />
      {soundPopup &&
        createPortal(
          <div
            ref={soundPopupRef}
            className="fixed z-50 flex w-[240px] flex-col gap-2 rounded-[12px] border border-[#E2E8F0] bg-white p-3 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
            style={{ left: soundPopup.x, top: soundPopup.y + 4 }}
          >
            <input
              ref={soundSearchRef}
              type="text"
              value={soundSearch}
              onChange={(e) => setSoundSearch(e.target.value)}
              placeholder="Rechercher un son…"
              className="w-full rounded-[8px] border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-[12px] outline-none placeholder:text-[#94a3b8] focus:border-[#C8009C]"
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") {
                  e.preventDefault();
                  setSoundPopup(null);
                }
              }}
            />
            <div className="flex max-h-[180px] flex-col gap-1 overflow-y-auto">
              {soundsLoading && <span className="text-[11px] text-[#94a3b8] px-1">Chargement…</span>}
              {!soundsLoading && sounds.length === 0 && (
                <span className="text-[11px] text-[#94a3b8] px-1">Aucun son disponible</span>
              )}
              {sounds
                .filter((s) => {
                  const q = soundSearch.trim().toLowerCase();
                  if (!q) return true;
                  return s.name.toLowerCase().includes(q) || (s.category ?? "").toLowerCase().includes(q);
                })
                .map((sound) => {
                  const label = sound.category || sound.name;
                  return (
                    <button
                      key={sound.path}
                      type="button"
                      onClick={() => handleSoundInsert(sound)}
                      className="flex items-center gap-2 rounded-[6px] px-2 py-1.5 text-left hover:bg-[#FDF2FB]"
                    >
                      <span className="inline-flex items-center rounded-[4px] border border-[#C8009C] bg-[#FDEBF9] px-1.5 py-0.5 text-[11px] font-medium text-[#7D005F]">
                        {label}
                      </span>
                      <span className="truncate text-[11px] text-[#64748B]">{sound.name}</span>
                    </button>
                  );
                })}
            </div>
          </div>,
          document.body
        )}
      {bracePopup &&
        createPortal(
          <div
            ref={bracePopupRef}
            className="fixed z-50 flex w-[200px] flex-col gap-2 rounded-[12px] border border-[#E2E8F0] bg-white p-3 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
            style={{ left: bracePopup.x, top: bracePopup.y + 4 }}
          >
            <input
              ref={braceInputRef}
              type="text"
              value={braceCustom}
              onChange={(e) => setBraceCustom(e.target.value)}
              placeholder="Écrire une expression…"
              className="w-full rounded-[8px] border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-[12px] outline-none placeholder:text-[#94a3b8] focus:border-[#623DC7]"
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (braceCustom.trim()) handleBraceInsert(` [${braceCustom.trim()}]`);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setBracePopup(null);
                }
              }}
            />
            {braceCustom.trim() && (
              <div className="flex">
                <DraggableExpressionChip label={braceCustom.trim()} onPick={handleBraceInsert} />
              </div>
            )}
            <div className="flex max-h-[140px] flex-col gap-1.5 overflow-y-auto">
              {EXPRESSION_PRESETS.map((expr) => (
                <DraggableExpressionChip key={expr} label={expr} onPick={handleBraceInsert} />
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
